import {
    AdminPanelSettings,
    ArrowBack,
    ArrowForward,
    Difference,
    Settings,
    Sync,
    ViewModule,
} from '@mui/icons-material';
import {
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { useContent } from 'contexts/Content/Content';
import { useSettings } from 'contexts/Settings/Settings';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import * as setup from 'setup';
import useResizeObserver from 'use-resize-observer';
import SettingsStats from '../SettingsStats/SettingsStats';

export default function SettingsControls() {
    const [open, setOpen] = useState(false);
    const content = useContent();
    const router = useRouter();
    const settings = useSettings();
    const containerObserver = useResizeObserver<HTMLDivElement>();

    const debugs = useMemo<SettingsControlsDebug[]>(() => {
        return Object.keys(settings)
            .filter(name => name.startsWith('debug'))
            .map(name => ({
                label: name.replace('debug', ''),
                name,
                reloader: settings.reloaders.includes(name),
            }));
    }, [settings]);

    const lesson = useMemo(() => {
        return /lessons\/(.*)/.exec(router.asPath)?.[1] || null;
    }, [router.asPath]);

    const prevLesson = useMemo(() => {
        const lessonIndex = lesson ? setup.lessonKeys.indexOf(lesson) : -1;
        return setup.lessonKeys[lessonIndex - 1] || null;
    }, [lesson]);

    const nextLesson = useMemo(() => {
        const lessonIndex = lesson ? setup.lessonKeys.indexOf(lesson) : -1;
        return setup.lessonKeys[lessonIndex + 1] || null;
    }, [lesson]);

    const padding = useMemo(() => {
        return ((open || settings.debugStats) && containerObserver.height) || 0;
    }, [containerObserver.height, open, settings.debugStats]);

    function handleChange(e: any) {
        let { name, type, value } = e.target;

        if (type === 'checkbox') {
            value = e.target.checked;
        } else if (type === 'number') {
            value = value ? parseFloat(value) : 1;
        }

        settings.set(name, value);
    }

    function handleClearHistory() {
        if (settings.debugProgress) {
            window.location.reload();
        } else {
            window.location.href = process.env.NEXT_CONFIG_BASE_PATH || '/';
        }
    }

    function handleLessonChange(e: any) {
        router.push(`/lessons/${e.target.value}`);
    }

    return (
        <>
            <Box bottom={0} color="grey" left={0} position="fixed" zIndex={1200}>
                <IconButton
                    aria-label={`${open ? 'close' : 'open'} settings`}
                    color="inherit"
                    onClick={() => setOpen(!open)}
                >
                    <Settings />
                </IconButton>
            </Box>

            {(open || settings.debugStats) && (
                <>
                    <Box height={padding} marginTop={5} />
                    <Box bottom={-16} left={60} position="fixed" ref={containerObserver.ref} right={60} zIndex={1200}>
                        <Paper elevation={8}>
                            <Box paddingBottom={3} paddingX={2} paddingTop={1}>
                                {settings.debugStats && (
                                    <>
                                        <SettingsStats />
                                        {open && (
                                            <Box marginY={2}>
                                                <Divider />
                                            </Box>
                                        )}
                                    </>
                                )}
                                {open && (
                                    <Box display="flex" flexDirection="row" gap={2}>
                                        <Box flex={3}>
                                            <Typography paragraph>Lessons</Typography>
                                            <Box display="flex" flexDirection="column" gap={2}>
                                                <Box display="inline-block" whiteSpace="nowrap">
                                                    <IconButton
                                                        size="small"
                                                        title="Clear history"
                                                        onClick={handleClearHistory}
                                                    >
                                                        <Sync />
                                                    </IconButton>
                                                    <IconButton
                                                        component={NextLink}
                                                        disabled={!prevLesson}
                                                        href={`/lessons/${prevLesson}`}
                                                        size="small"
                                                        title="Previous"
                                                    >
                                                        <ArrowBack />
                                                    </IconButton>
                                                    <IconButton
                                                        component={NextLink}
                                                        disabled={!nextLesson}
                                                        href={`/lessons/${nextLesson}`}
                                                        size="small"
                                                        title="Next"
                                                    >
                                                        <ArrowForward />
                                                    </IconButton>
                                                    <IconButton
                                                        component={NextLink}
                                                        href="/admin/lessons"
                                                        size="small"
                                                        title="Lessons overview"
                                                    >
                                                        <ViewModule />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        title="Reload content"
                                                        onClick={content.reload}
                                                    >
                                                        <Difference />
                                                    </IconButton>
                                                    <IconButton
                                                        component={NextLink}
                                                        href="/admin"
                                                        size="small"
                                                        title="Admin panel"
                                                    >
                                                        <AdminPanelSettings />
                                                    </IconButton>
                                                </Box>
                                                <TextField
                                                    fullWidth
                                                    label="Lesson"
                                                    name="lesson"
                                                    select
                                                    value={lesson || ''}
                                                    onChange={handleLessonChange}
                                                >
                                                    {setup.lessonKeys.map((lesson, index) => (
                                                        <MenuItem
                                                            divider={setup.lessonKeys[index + 1]?.length === 1}
                                                            key={index}
                                                            sx={{ fontWeight: lesson.length === 1 ? 600 : 400 }}
                                                            value={lesson}
                                                        >
                                                            {lesson}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Box>
                                        </Box>

                                        <Divider flexItem orientation="vertical" />

                                        <Box flex={2}>
                                            <Typography paragraph>Game</Typography>
                                            <Box display="flex" flexDirection="column" gap={2}>
                                                <TextField
                                                    color="info"
                                                    inputProps={{ max: 1, min: 0.01, step: 0.01 }}
                                                    label="Volume"
                                                    name="volume"
                                                    type="number"
                                                    value={settings.volume}
                                                    onChange={handleChange}
                                                />
                                            </Box>
                                        </Box>

                                        <Divider flexItem orientation="vertical" />

                                        <Box flex={8}>
                                            <Typography paragraph>Debugs</Typography>
                                            <Box
                                                display="grid"
                                                sx={{
                                                    gridTemplateColumns: {
                                                        sm: '1fr',
                                                        md: '1fr 1fr',
                                                        lg: '1fr 1fr 1fr',
                                                        xl: '1fr 1fr 1fr 1fr',
                                                    },
                                                }}
                                            >
                                                {debugs.map((debug, index) => (
                                                    <Box key={index}>
                                                        <FormControlLabel
                                                            color="info"
                                                            componentsProps={{
                                                                typography: {
                                                                    variant: 'montserrat414',
                                                                },
                                                            }}
                                                            control={
                                                                <Checkbox
                                                                    checked={settings[debug.name]}
                                                                    color="info"
                                                                    name={debug.name}
                                                                    size="small"
                                                                    sx={{ padding: 0.5 }}
                                                                    value=""
                                                                    onChange={handleChange}
                                                                />
                                                            }
                                                            label={`${debug.label}${debug.reloader ? '*' : ''}`}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Box>
                </>
            )}
        </>
    );
}
